/**
 * DemoBlankWizard — Modal plein écran affiché aux nouveaux comptes démo
 * qui ont choisi le mode "blank" (base vierge).
 *
 * 8 étapes guidées avec aide contextuelle. L'utilisateur peut :
 *  - Compléter l'étape (bouton "Suivant")
 *  - Passer cette étape (bouton "Plus tard")
 *  - Quitter le wizard (croix en haut à droite)
 *
 * L'état est persisté côté backend via /api/demo/wizard-progress.
 */
import React, { useEffect, useState } from 'react';
import {
  X, Check, ArrowRight, ArrowLeft, Building2, Palette, BookOpen,
  Briefcase, Users, Calendar, FileText, Mail, Sparkles, ExternalLink, Loader2,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../api/client';

const STEPS = [
  {
    id: 'org_info', icon: Building2,
    title: 'Informations sur votre organisme',
    description: "Raison sociale, SIRET, numéro de déclaration d'activité (NDA), agrément Qualiopi. Ces infos apparaîtront sur tous vos documents (devis, factures, conventions).",
    cta: 'Compléter', target: '/app/parametres?tab=org',
    help: "Vous trouverez votre NDA sur https://entreprendre.service-public.fr — la déclaration d'activité est obligatoire en France pour facturer de la formation pro.",
  },
  {
    id: 'branding', icon: Palette,
    title: 'Personnalisez votre marque',
    description: "Logo, couleurs primaire et secondaire, en-têtes des documents. Votre identité visuelle apparaîtra sur tous les PDFs envoyés à vos clients.",
    cta: 'Personnaliser', target: '/app/parametres?tab=branding',
    help: "Format logo : PNG ou SVG avec fond transparent, 200×60px minimum. Les couleurs s'appliquent automatiquement aux boutons et accents.",
  },
  {
    id: 'program', icon: BookOpen,
    title: 'Créez votre premier programme',
    description: "Un programme est une formation type que vous pouvez vendre plusieurs fois. Définissez son titre, durée, objectifs, prérequis et tarif par participant.",
    cta: 'Créer un programme', target: '/app/programmes',
    help: "Le programme est la brique de base : à partir d'un programme, vous créerez des sessions (instances datées) pour vos clients.",
  },
  {
    id: 'company', icon: Briefcase,
    title: 'Ajoutez votre première entreprise cliente',
    description: "Les entreprises sont vos clients facturés. Vous pouvez aussi gérer des particuliers en parallèle.",
    cta: 'Ajouter une entreprise', target: '/app/entreprises',
    help: "Le SIRET est important : il permet la vérification automatique via API gouvernementale et l'identification CPF/OPCO.",
  },
  {
    id: 'learners', icon: Users,
    title: 'Inscrivez vos premiers stagiaires',
    description: "Les stagiaires sont les personnes qui suivront vos formations. Vous pouvez les rattacher à une entreprise ou les gérer en individuel.",
    cta: 'Gérer les stagiaires', target: '/app/stagiaires',
    help: "Astuce : importez en masse via CSV pour gagner du temps si vous avez beaucoup de stagiaires.",
  },
  {
    id: 'session', icon: Calendar,
    title: 'Planifiez votre première session',
    description: "Une session est une formation datée avec des participants, un formateur et un lieu. C'est le cœur opérationnel d'OFManager.",
    cta: 'Créer une session', target: '/app/sessions',
    help: "La session déclenche automatiquement la convocation, l'émargement, la convention, le devis et la facture associés.",
  },
  {
    id: 'quote', icon: FileText,
    title: 'Émettez votre premier devis',
    description: "Générez un devis PDF prêt à envoyer en moins de 2 minutes. Possibilité de signature électronique directement par votre client.",
    cta: 'Créer un devis', target: '/app/devis',
    help: "Le devis se génère à partir d'une session OU d'un programme. Il peut être validé par signature électronique ou simple validation email.",
  },
  {
    id: 'team', icon: Mail,
    title: 'Invitez votre équipe & configurez les emails',
    description: "Invitez vos formateurs et collaborateurs avec des rôles dédiés. Configurez Resend ou votre SMTP pour les envois automatiques (devis, convocations, factures).",
    cta: "Gérer l'équipe", target: '/app/parametres?tab=team',
    help: "Bonus : si vous invitez 2 collaborateurs ou plus, votre essai gratuit est automatiquement étendu de 7 jours supplémentaires.",
  },
];

const STORAGE_KEY = 'ofm_demo_blank_wizard_closed';

export default function DemoBlankWizard() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [stepIdx, setStepIdx] = useState(0);
  const [progress, setProgress] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (localStorage.getItem(STORAGE_KEY) === '1') return;
    api.get('/demo/wizard-progress')
      .then(({ data }) => {
        if (cancelled) return;
        if (data?.is_demo && data?.wizard_required && data?.seed_mode === 'blank') {
          setProgress(data.progress || {});
          setOpen(true);
          // Resume on first uncompleted step
          const firstIncomplete = STEPS.findIndex(s => !data.progress?.[s.id]);
          setStepIdx(firstIncomplete >= 0 ? firstIncomplete : 0);
        }
      })
      .catch(() => { /* ignore */ });
    return () => { cancelled = true; };
  }, []);

  if (!open) return null;

  const step = STEPS[stepIdx];
  const Icon = step.icon;
  const totalSteps = STEPS.length;
  const completedCount = Object.values(progress).filter(Boolean).length;
  const pct = Math.round((completedCount / totalSteps) * 100);

  const markStep = async (completed) => {
    setLoading(true);
    try {
      await api.post('/demo/wizard-progress', { step: step.id, completed });
      setProgress({ ...progress, [step.id]: completed });
    } catch { /* ignore */ } finally { setLoading(false); }
  };

  const handleSkip = async () => {
    await markStep(false);
    if (stepIdx < totalSteps - 1) setStepIdx(stepIdx + 1);
    else handleClose();
  };

  const handleNext = async () => {
    await markStep(true);
    if (stepIdx < totalSteps - 1) setStepIdx(stepIdx + 1);
    else handleClose();
  };

  const handleAction = async () => {
    await markStep(true);
    handleClose();
    navigate(step.target);
  };

  const handleClose = () => {
    localStorage.setItem(STORAGE_KEY, '1');
    setOpen(false);
  };

  return (
    <div
      data-testid="demo-blank-wizard"
      className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4"
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 pt-5 pb-4 border-b border-slate-100 flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Sparkles className="w-4 h-4 text-[#4F46E5]" />
              <span className="text-xs font-bold uppercase tracking-wide text-[#4338CA]">
                Assistant de configuration
              </span>
            </div>
            <p className="text-xs text-slate-500">
              Étape {stepIdx + 1} sur {totalSteps} — {completedCount} étape{completedCount > 1 ? 's' : ''} complétée{completedCount > 1 ? 's' : ''} ({pct}%)
            </p>
          </div>
          <button
            onClick={handleClose}
            data-testid="demo-blank-wizard-close"
            className="text-slate-400 hover:text-slate-700 rounded-full p-1.5 hover:bg-slate-100"
            aria-label="Fermer le wizard"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Progress bar */}
        <div className="px-6 py-2 bg-slate-50 border-b border-slate-100">
          <div className="flex gap-1">
            {STEPS.map((s, i) => (
              <div
                key={s.id}
                className={`h-1.5 flex-1 rounded-full transition-all ${
                  progress[s.id] ? 'bg-[#6366F1]' : i === stepIdx ? 'bg-indigo-300' : 'bg-slate-200'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-6 overflow-y-auto flex-1">
          <div className="flex items-start gap-4 mb-4">
            <div className="w-12 h-12 rounded-xl bg-indigo-100 flex items-center justify-center flex-shrink-0">
              <Icon className="w-6 h-6 text-[#4F46E5]" />
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-bold text-slate-900 mb-2">{step.title}</h3>
              <p className="text-sm text-slate-600 leading-relaxed">{step.description}</p>
            </div>
          </div>

          {/* Help box */}
          {step.help && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900 leading-relaxed">
              <strong>💡 Astuce : </strong>{step.help}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-100 bg-slate-50 flex items-center justify-between flex-wrap gap-2">
          <button
            onClick={() => stepIdx > 0 && setStepIdx(stepIdx - 1)}
            disabled={stepIdx === 0}
            data-testid="demo-blank-wizard-prev"
            className="text-xs font-semibold text-slate-500 hover:text-slate-900 disabled:opacity-30 inline-flex items-center gap-1 px-3 py-2"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Précédent
          </button>

          <div className="flex items-center gap-2">
            <button
              onClick={handleSkip}
              disabled={loading}
              data-testid="demo-blank-wizard-skip"
              className="text-xs font-semibold text-slate-600 hover:text-slate-900 px-3 py-2 rounded-lg hover:bg-slate-100"
            >
              Plus tard
            </button>
            <button
              onClick={handleAction}
              disabled={loading}
              data-testid="demo-blank-wizard-action"
              className="text-xs font-semibold text-white bg-[#4F46E5] hover:bg-[#4338CA] px-4 py-2 rounded-lg inline-flex items-center gap-1.5 disabled:opacity-50"
            >
              {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ExternalLink className="w-3.5 h-3.5" />}
              {step.cta}
            </button>
            <button
              onClick={handleNext}
              disabled={loading}
              data-testid="demo-blank-wizard-next"
              className="text-xs font-semibold text-[#4338CA] border border-indigo-200 hover:bg-indigo-50 px-3 py-2 rounded-lg inline-flex items-center gap-1"
            >
              {stepIdx === totalSteps - 1 ? (
                <>Terminer <Check className="w-3.5 h-3.5" /></>
              ) : (
                <>Suivant <ArrowRight className="w-3.5 h-3.5" /></>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
